import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Ruler, Calculator, BookOpen } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { PatternPreviewCard } from "@/components/landing/PatternPreviewCard";
import { NewsletterSignup } from "@/components/newsletter/NewsletterSignup";
import { supabase } from "@/integrations/supabase/client";

const Index = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = user !== null;

  // Redirect authenticated users to their dashboard.
  // If they have projects → My Knits. If not → Pattern Generator to get started.
  useEffect(() => {
    if (!isLoggedIn || !user) return;

    const redirect = async () => {
      const { data } = await supabase
        .from("pattern_projects")
        .select("id")
        .eq("user_id", user.id)
        .limit(1);

      if (data && data.length > 0) {
        navigate("/studio", { replace: true });
      } else {
        navigate("/pattern-generator", { replace: true });
      }
    };

    redirect();
  }, [isLoggedIn, user, navigate]);

  const scrollToHowItWorks = () => {
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  // While the redirect is resolving, render nothing to avoid a flash of the marketing page
  if (isLoggedIn) return null;

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-yarn-woolWhite via-yarn-ivory to-yarn-quartz/50 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='24' height='20' viewBox='0 0 24 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23666666' stroke-width='0.5'%3E%3Cpath d='M2 4c2 0 4 2 6 2s4-2 6-2 4 2 6 2 4-2 6-2M2 12c2 0 4 2 6 2s4-2 6-2 4 2 6 2 4-2 6-2M8 8c2 0 4 2 6 2s4-2 6-2 4 2 6 2M8 16c2 0 4 2 6 2s4-2 6-2 4 2 6 2'/%3E%3C/g%3E%3C/svg%3E\")",
          }}
        />

        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16 max-w-5xl mx-auto">
            {/* Left: Text */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-playfair bg-gradient-to-r from-primary via-yarn-mulberry to-yarn-copper bg-clip-text text-transparent leading-tight">
                Your gauge. Your measurements. Your pattern.
              </h1>
              <p className="text-lg text-yarn-slate/80 leading-relaxed mb-8 max-w-lg">
                No gauge math. No size modifications. No adapting someone else's numbers. Patterns
                generated for you, with tutorials and stitch demonstrations built in.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Button
                  size="lg"
                  onClick={() => navigate("/register")}
                  className="bg-primary hover:bg-primary/90 text-white font-medium px-8 py-3 rounded-lg shadow-sm"
                >
                  Create Your Free Account
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={scrollToHowItWorks}
                  className="border-yarn-mulberry/20 text-yarn-slate hover:bg-yarn-mulberry/10 font-medium px-8 py-3 rounded-lg"
                >
                  See How It Works
                </Button>
              </div>
            </div>

            {/* Right: Pattern preview card */}
            <div className="flex-shrink-0 w-full max-w-xs md:max-w-sm">
              <PatternPreviewCard />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 bg-yarn-woolWhite">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 text-center text-yarn-slate font-playfair">
            How it works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: 1,
                label: "Pick your project",
                body: "Pick your project type, construction method, and stitch patterns. Basics, textures, cables, and lace — with more on the way.",
              },
              {
                step: 2,
                label: "Enter your numbers",
                body: "Plug in your desired measurements and pick your notions. Metric or imperial — whatever matches how you knit.",
              },
              {
                step: 3,
                label: "Get a pattern that fits",
                body: "Your pattern is calculated to your exact specifications. We suggest a swatch size so you can recalibrate before you cast on. Tutorials and stitch demonstrations are built right in, at every step where you might need them.",
              },
            ].map(({ step, label, body }) => (
              <div key={step} className="text-center">
                <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {step}
                </div>
                <h3 className="text-xl font-bold mb-2 text-yarn-slate">{label}</h3>
                <p className="text-yarn-slate/80 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="container mx-auto px-6">
        <div className="border-t border-yarn-quartz" />
      </div>

      {/* Value Proposition Cards */}
      <section className="py-16 bg-gradient-to-br from-yarn-quartz/50 to-yarn-cream/30">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-yarn-woolWhite/90 border-yarn-copper/20 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="pt-8 pb-6">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-yarn-copper to-yarn-copper/70 rounded-full flex items-center justify-center mx-auto text-white shadow-sm">
                    <Ruler size={32} />
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-center text-yarn-slate">
                  Patterns That Actually Fit
                </h3>
                <p className="text-center text-yarn-slate/80">
                  Every pattern is generated for your gauge and your measurements. No choosing
                  between sizes and hoping for the best. No recalculating someone else's math.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-yarn-woolWhite/90 border-yarn-juniper/20 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="pt-8 pb-6">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-yarn-juniper to-yarn-juniper/70 rounded-full flex items-center justify-center mx-auto text-white shadow-sm">
                    <Calculator size={32} />
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-center text-yarn-slate">
                  No More Pattern Math
                </h3>
                <p className="text-center text-yarn-slate/80">
                  You pick what you want to make. We handle stitch counts, shaping, and sizing. The
                  gauge swatch you actually knit is the gauge swatch that actually matters.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-yarn-woolWhite/90 border-primary/20 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="pt-8 pb-6">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/70 rounded-full flex items-center justify-center mx-auto text-white shadow-sm">
                    <BookOpen size={32} />
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-center text-yarn-slate">
                  Everything in One Place
                </h3>
                <p className="text-center text-yarn-slate/80">
                  Every stitch technique in your pattern comes with a built-in tutorial or
                  demonstration. No hunting through YouTube. No flipping between tabs and your
                  pattern.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-16 bg-yarn-woolWhite">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="flex-shrink-0">
              <img
                src="https://wvgoatayeagzvkpzmunv.supabase.co/storage/v1/object/public/site-content/Founder%20Sketch.png"
                alt="Sarah, founder of IntelliStitch"
                className="w-40 h-40 md:w-48 md:h-48 rounded-full object-cover border-4 border-yarn-quartz"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-4 text-yarn-slate font-playfair">
                Built by a knitter who got tired of the math
              </h2>
              <p className="text-yarn-slate/80 leading-relaxed mb-3">
                I'm Sarah. I have a day job, a yarn stash that's gotten slightly out of hand, and a
                long-standing frustration with pattern math. IntelliStitch started as a side project
                born from a simple question: why do knitters still have to do all this calculation
                themselves?
              </p>
              <p className="text-yarn-slate/80 leading-relaxed mb-4">
                I'd rather be knitting than building a personal brand — but I wanted you to know
                there's a real knitter behind this tool, not a tech company chasing the craft market.
                Every pattern IntelliStitch generates is tested against the same standards I'd hold
                for my own projects.
              </p>
              <p className="text-sm text-yarn-mulberry">Made in Calgary, Alberta</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 bg-gradient-to-br from-yarn-quartz/50 to-yarn-cream/30">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <h2 className="text-3xl font-bold mb-4 text-yarn-slate font-playfair">
            Honest pricing, no subscriptions
          </h2>
          <p className="text-lg text-yarn-slate/80 leading-relaxed mb-3">
            Patterns from $4–7 depending on your credit pack. Buy credits when you need them, use
            them at your own pace. No monthly fees. No expiry dates. Just pay for what you make.
          </p>
          <p className="text-sm text-yarn-slate/60 mb-6">
            During beta, all accounts have access to test credit card codes to buy credits and
            explore the full tool.
          </p>
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate("/pricing")}
            className="border-yarn-mulberry/20 text-yarn-slate hover:bg-yarn-mulberry/10 font-medium px-8 py-3 rounded-lg"
          >
            View Pricing
          </Button>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-yarn-woolWhite">
        <div className="container mx-auto px-6 max-w-xl">
          <NewsletterSignup variant="card" />
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary via-yarn-mulberry to-yarn-copper text-white">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <h2 className="text-3xl font-bold mb-4">Come build with us</h2>
          <p className="text-lg mb-8 text-white/90 leading-relaxed">
            IntelliStitch is in beta — the tool is live, the patterns are real, and your feedback
            shapes what comes next. Create an account to explore everything with free test credits.
            No commitment, no credit card required.
          </p>
          <Button
            size="lg"
            onClick={() => navigate("/register")}
            className="bg-yarn-woolWhite text-yarn-slate hover:bg-yarn-woolWhite/90 font-medium px-8 py-3 rounded-lg shadow-lg"
          >
            Create Your Free Account
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
